import React from 'react';
import { Box, Button, IconButton, ListSubheader, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import { keyframes } from '@mui/system';
import { FilterAltOffOutlined, RefreshRounded } from '@mui/icons-material';
import { SALES_PROJECT_TYPES } from '../../utils/commercialPlanning';
import {
  CommercialDashboard,
  CommercialDashboardFilters,
  formatUpdatedAt,
} from '../../utils/commercialDashboard';
import { useConsolePalette } from './consolePalette';

const pulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.35; }
  100% { opacity: 1; }
`;

type DashboardFilterBarProps = {
  dashboard: CommercialDashboard;
  filters: CommercialDashboardFilters;
  onChange: (next: CommercialDashboardFilters) => void;
  refreshing: boolean;
  error: string | null;
  onRefresh: () => void;
};

const ALL = '';

const DashboardFilterBar: React.FC<DashboardFilterBarProps> = ({
  dashboard,
  filters,
  onChange,
  refreshing,
  error,
  onRefresh,
}) => {
  const palette = useConsolePalette();
  const quarters = dashboard.periods.filter((period) => period.key.includes('-Q'));
  const years = dashboard.periods.filter((period) => !period.key.includes('-Q'));
  const periodKey = filters.period ?? dashboard.period.key;
  const knownPeriod = dashboard.periods.some((period) => period.key === periodKey);
  const hasScope = filters.client_id != null || Boolean(filters.project_type) || filters.owner_id != null;

  const selectSx = {
    minWidth: { xs: '100%', sm: 180 },
    flex: { xs: '1 1 100%', sm: '0 0 auto' },
    '& .MuiOutlinedInput-root': {
      bgcolor: palette.inset,
      fontSize: '0.85rem',
      '& fieldset': { borderColor: palette.border },
    },
  } as const;

  const statusColor = error ? palette.fall : palette.rise;
  const statusText = error
    ? `Refresh failed · showing data from ${formatUpdatedAt(dashboard.generated_at)}`
    : `Data updated ${formatUpdatedAt(dashboard.generated_at)}`;

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.5,
        border: `1px solid ${palette.border}`,
        borderRadius: 1,
        bgcolor: palette.surface,
      }}
    >
      <TextField
        select
        size="small"
        label="Period"
        value={periodKey}
        onChange={(event) => onChange({ ...filters, period: event.target.value })}
        sx={selectSx}
      >
        <ListSubheader>Quarters</ListSubheader>
        {quarters.map((period) => (
          <MenuItem key={period.key} value={period.key}>{period.label}</MenuItem>
        ))}
        <ListSubheader>Years</ListSubheader>
        {years.map((period) => (
          <MenuItem key={period.key} value={period.key}>{period.label}</MenuItem>
        ))}
        {!knownPeriod && <MenuItem value={periodKey}>{dashboard.period.label}</MenuItem>}
      </TextField>

      <TextField
        select
        size="small"
        label="Client"
        value={filters.client_id != null ? String(filters.client_id) : ALL}
        onChange={(event) => onChange({
          ...filters,
          client_id: event.target.value === ALL ? null : Number(event.target.value),
        })}
        sx={selectSx}
      >
        <MenuItem value={ALL}>All clients</MenuItem>
        {dashboard.filter_options.clients.map((client) => (
          <MenuItem key={client.id} value={String(client.id)}>{client.name}</MenuItem>
        ))}
      </TextField>

      <TextField
        select
        size="small"
        label="Project type"
        value={filters.project_type ?? ALL}
        onChange={(event) => onChange({ ...filters, project_type: event.target.value || null })}
        sx={selectSx}
      >
        <MenuItem value={ALL}>All types</MenuItem>
        {SALES_PROJECT_TYPES.map((type) => (
          <MenuItem key={type} value={type}>{type}</MenuItem>
        ))}
      </TextField>

      <TextField
        select
        size="small"
        label="Responsible"
        value={filters.owner_id != null ? String(filters.owner_id) : ALL}
        onChange={(event) => onChange({
          ...filters,
          owner_id: event.target.value === ALL ? null : Number(event.target.value),
        })}
        sx={selectSx}
      >
        <MenuItem value={ALL}>Everyone</MenuItem>
        {dashboard.filter_options.owners.map((owner) => (
          <MenuItem key={owner.id} value={String(owner.id)}>{owner.name}</MenuItem>
        ))}
      </TextField>

      {hasScope && (
        <Button
          size="small"
          startIcon={<FilterAltOffOutlined fontSize="small" />}
          onClick={() => onChange({ period: filters.period })}
          sx={{ color: palette.inkSoft, textTransform: 'none', fontWeight: 600 }}
        >
          Clear
        </Button>
      )}

      <Box sx={{ ml: { md: 'auto' }, display: 'flex', alignItems: 'center', gap: 1 }} role="status" aria-live="polite">
        <Box
          aria-hidden
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: statusColor,
            animation: refreshing ? `${pulse} 1.2s ease-in-out infinite` : 'none',
          }}
        />
        <Typography variant="caption" sx={{ color: error ? palette.fall : palette.inkSoft }}>
          {statusText}
        </Typography>
        <Tooltip title="Refresh now">
          <IconButton size="small" onClick={onRefresh} aria-label="Refresh dashboard" sx={{ color: palette.inkSoft }}>
            <RefreshRounded fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default DashboardFilterBar;
